import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { useGetBranches } from "src/api/generic";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { authState } from "src/store/auth";
import { useGetAccountTree } from "src/api/tree/tree";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import AccountTreeDropdown, {
  TreeNode,
} from "src/shared/components/account-tree-dropdown";
import {
  useAddManualJournalMutation,
  useUpdateManualJournalMutation,
} from "src/api/events/manual-journals";
import { convertObjectToFormData } from "src/utils";
import { ConfirmPopup } from "src/shared/components/confirm-popup";

interface IProps {
  open: boolean;
  handleClose: () => void;
  dataToEdit?: any;
  refetch: Function;
  isViewing?: boolean;
}

const AddEditJournalPopup = (props: IProps) => {
  const { open, handleClose, dataToEdit, refetch, isViewing } = props;
  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");
  const [files, setFiles] = useState<any>([]);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [auth] = useRecoilState(authState);
  const { user } = auth;
  const companyCloseDate = user.company?.close_date;
  const companyName = user.company?.name;
  const [isSaveAsDraft, setIsSaveAsDraft] = useState<boolean>(false);
  const [toConfirmClose, setToConfirmClose] = useState(false);

  const HeadText = [
    t("account"),
    generalT("description"),
    generalT("debit"),
    generalT("credit"),
    generalT("actions"),
  ];

  const schema = yup.object().shape({
    mode: yup
      .string()
      .required(t("required"))
      .oneOf(["add", "edit"] as const),
    branch_id: yup.number().required("Required"),
    date: yup.date().required("Required"),
    note: yup.string().nullable(),
    items: yup
      .array()
      .of(
        yup.object().shape({
          item_id: yup.number(),
          chart_account_id: yup.string().required("Required"),
          chart_account_placement_id: yup.string().required("Required"),
          chart_account_name: yup.string().required("Required"),
          description: yup.string().required("Required"),
          debit: yup
            .number()
            .typeError("Must be a number")
            .min(0, "Negative values are not allowed")
            .required("Required"),
          credit: yup
            .number()
            .typeError("Must be a number")
            .min(0, "Negative values are not allowed")
            .required("Required"),
        }),
      )
      .min(1, "At least one item is required")
      .required("Required"),
  });

  interface FormInputs extends yup.InferType<typeof schema> {}

  //React hook form
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    reset,
    setValue,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      mode: "add",
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "items",
    control,
  });

  const selectedAccountPlacements = watch("items")?.map(
    (item) => item.chart_account_placement_id,
  );

  const handleTreeAccountSelect = (node: TreeNode) => {
    if (
      !selectedAccountPlacements.includes(`${node.chart_account_placement_id}`)
    ) {
      append({
        chart_account_name: node.name,
        chart_account_id: `${node.id}`,
        chart_account_placement_id: `${node.chart_account_placement_id}`,
        description: "",
        debit: 0,
        credit: 0,
      });
    }
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
  };

  const handleCancelConfirm = () => setToConfirmClose(false);

  const handleOpenCloseConfirm = () => {
    if (mode === "add") {
      setToConfirmClose(true);
    } else {
      onClose();
    }
  };

  // APIS
  const { mutate, isPending, status, error } = useAddManualJournalMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateManualJournalMutation();
  const { data: branchesData } = useGetBranches();
  const { data: accountsTreeData, isLoading: isLoadingAccounts } =
    useGetAccountTree({ show_employee_accounts: 1 });

  const mode = getValues("mode");

  const selectedBranch = branchesData?.data.find((branch) => {
    return branch.id === +watch("branch_id");
  });

  const items = watch("items") || [];

  const { totalDebit, totalCredit } = items.reduce(
    (acc, item) => {
      acc.totalDebit += Number(item.debit || 0);
      acc.totalCredit += Number(item.credit || 0);
      return acc;
    },
    { totalDebit: 0, totalCredit: 0 },
  );

  const difference = Math.abs(totalDebit - totalCredit);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const { branch_id, date, note, items } = data;

    // Company Lock date validation
    if (
      !!companyCloseDate &&
      moment(watch("date")).isSameOrBefore(moment(companyCloseDate))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT(
            "canNotAddEditOrDeleteBeforeLockDateCompany",
          )}: ${companyCloseDate}`,
        },
      ]);
      return;
    }

    //It should have no difference
    if (difference !== 0) {
      return;
    }

    const toBeSentData = {
      status: isSaveAsDraft ? 0 : 1,
      date: moment(date).format("YYYY-MM-DD"),
      ...(branch_id !== 0 ? { branch_id: branch_id } : {}),
      note: note ?? "",
    };

    const formData = convertObjectToFormData(toBeSentData);

    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }

    // append items
    if (items.length > 0) {
      items.forEach((item, index: number) => {
        formData.append(
          `items[${index}][chart_account_id]`,
          `${item.chart_account_id}`,
        );
        formData.append(
          `items[${index}][chart_account_placement_id]`,
          `${item.chart_account_placement_id}`,
        );
        formData.append(`items[${index}][description]`, `${item.description}`);
        formData.append(`items[${index}][credit]`, `${item.credit}`);
        formData.append(`items[${index}][debit]`, `${item.debit}`);
        formData.append(`items[${index}][vat]`, `0`);
      });
    }

    if (mode === "add") {
      mutate(formData);
    } else {
      mutateUpdate({
        id: dataToEdit.id,
        ...toBeSentData,
        items: items.map((item) => {
          return {
            id: item.item_id,
            chart_account_id: +item.chart_account_id,
            chart_account_placement_id: +item.chart_account_placement_id,
            description: item.description,
            debit: +item.debit,
            credit: +item.credit,
            vat: 0,
          };
        }),
      });
    }
  };

  const onClose = () => {
    handleClose();
    reset({});
  };

  useEffect(() => {
    if (dataToEdit) {
      reset({
        mode: "edit",
        date: moment(dataToEdit?.date, "YYYY-MM-DD").toDate(),
        branch_id: dataToEdit?.branch?.id ?? 0,
        note: dataToEdit?.note ?? "",
        items: dataToEdit?.items.map((item: any) => {
          return {
            item_id: item?.id,
            chart_account_id: `${item?.chart_account_id}`,
            chart_account_placement_id: `${item?.chart_account_placement_id}`,
            chart_account_name: item?.item_name,
            description: item?.description,
            debit: item?.debit,
            credit: item?.credit,
          };
        }),
      });
    }
  }, [status]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  return (
    <Box>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Stack
          spacing={3}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { sx: "100%", lg: "80%", xl: "70%" },
            bgcolor: "#FFF",
            border: "1px solid #D0D5DD",
            borderRadius: 1,
            boxShadow: 24,
            p: 3,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            p={0}
          >
            <Typography color={"#101828"} fontSize={"20px"} lineHeight={"24px"}>
              {mode === "add" ? t("addJournal") : t("editJournal")}
            </Typography>
            <IconButton onClick={handleOpenCloseConfirm} sx={{ p: 0 }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.branch_id}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {generalT("branch")}
                    </InputLabel>
                    <Controller
                      name="branch_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={generalT("branch")}
                          disabled={isViewing}
                        >
                          <MenuItem value={0}>{companyName}</MenuItem>
                          {branchesData?.data?.map(({ id, name }) => {
                            return (
                              <MenuItem key={id} value={`${id}`}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {generalT(`${errors.branch_id?.message ?? ""}`)}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.date,
                            helperText: t(`${errors.date?.message ?? ""}`),
                            size: "small",
                            label: t("date"),
                          },
                        }}
                        format={"dd/MM/yyyy"}
                        disabled={isViewing}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Typography>{t("journalLines")}</Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      {HeadText.map((item) => (
                        <TableCell
                          key={item}
                          //
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: "18px",
                            bgcolor: "primary.light",
                          }}
                        >
                          {item}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((itemField, index) => {
                      return (
                        <TableRow
                          key={itemField.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell>{itemField?.chart_account_name}</TableCell>
                          <TableCell>
                            {/* description */}
                            <Controller
                              name={`items.${index}.description`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  label={t("description")}
                                  variant="outlined"
                                  {...field}
                                  error={!!errors.items?.[index]?.description}
                                  helperText={
                                    errors.items?.[index]?.description?.message
                                  }
                                  fullWidth
                                  size="small"
                                  sx={{ width: 180, maxWidth: 180 }}
                                  disabled={isViewing}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`items.${index}.debit`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  label={t("debit")}
                                  variant="outlined"
                                  {...field}
                                  error={!!errors.items?.[index]?.debit}
                                  helperText={
                                    errors.items?.[index]?.debit?.message
                                  }
                                  fullWidth
                                  size="small"
                                  sx={{ width: 130, maxWidth: 130 }}
                                  disabled={isViewing}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <Controller
                              name={`items.${index}.credit`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  label={t("credit")}
                                  variant="outlined"
                                  {...field}
                                  error={!!errors.items?.[index]?.credit}
                                  helperText={
                                    errors.items?.[index]?.credit?.message
                                  }
                                  fullWidth
                                  size="small"
                                  sx={{ width: 130, maxWidth: 130 }}
                                  disabled={isViewing}
                                />
                              )}
                            />
                          </TableCell>

                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleDeleteItem(index);
                              }}
                              disabled={isViewing}
                            >
                              <img
                                src={DeleteIcon}
                                alt=""
                                width={"16px"}
                                style={{ cursor: "pointer" }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {!isViewing ? (
                <AccountTreeDropdown
                  treeData={accountsTreeData?.data ?? []}
                  isLoading={isLoadingAccounts}
                  onSelect={handleTreeAccountSelect}
                  excludeIds={selectedAccountPlacements ?? []}
                  label={generalT("searchAndAddItem")}
                  sx={{ width: 255 }}
                />
              ) : null}
              {/* total summary */}
              <Stack p={2} gap={1.5} bgcolor={"#F9FAFB"}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {t("totalDebit")}
                  </Typography>
                  <Typography
                    fontSize={"14px"}
                    fontWeight={500}
                    color={"#344054"}
                  >
                    {totalDebit}
                  </Typography>
                </Stack>

                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {t("totalCredit")}
                  </Typography>
                  <Typography
                    fontSize={"14px"}
                    fontWeight={500}
                    color={"#344054"}
                  >
                    {totalCredit}
                  </Typography>
                </Stack>
                <Box height={"1px"} bgcolor={"#D0D5DD"} />
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography
                    fontSize={"14px"}
                    fontWeight={500}
                    color={"#344054"}
                  >
                    {generalT("difference")}
                  </Typography>
                  <Typography
                    fontSize={"14px"}
                    fontWeight={500}
                    color={difference !== 0 ? "error" : "green"}
                  >
                    {difference}
                  </Typography>
                </Stack>
              </Stack>

              {/* note */}
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={generalT("notesOptional")}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    maxRows={3}
                    {...field}
                    error={!!errors.note}
                    helperText={errors.note?.message}
                    disabled={isViewing}
                  />
                )}
              />

              {/* files */}
              {mode === "add" && (
                <Box width={"100%"} mt={2}>
                  <FilePondUploader
                    onUpload={(e) => setFiles(e)}
                    maxFiles={3}
                    maxFileSize={3}
                    acceptedFileTypes={[
                      "image/*",
                      "application/pdf",
                      "text/csv",
                      "application/vnd.ms-excel",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    ]}
                  />
                </Box>
              )}
            </Stack>

            {!isViewing && (
              <Stack spacing={2} direction={"row"} mt={3}>
                <Button
                  variant="outlined"
                  color="tertiary"
                  fullWidth
                  onClick={handleOpenCloseConfirm}
                >
                  {generalT("cancel")}
                </Button>
                {(mode === "add" ||
                  (mode === "edit" && !dataToEdit?.status)) && (
                  <LoadingButton
                    variant="contained"
                    fullWidth
                    type="submit"
                    loading={isPending || isPendingUpdate}
                    onClick={() => setIsSaveAsDraft(true)}
                  >
                    {mode == "add" ? generalT("saveAsDraft") : generalT("save")}
                  </LoadingButton>
                )}

                <LoadingButton
                  variant="contained"
                  fullWidth
                  type="submit"
                  loading={isPending || isPendingUpdate}
                  onClick={() => setIsSaveAsDraft(false)}
                >
                  {mode === "add" || (mode === "edit" && !dataToEdit?.status)
                    ? generalT("confirm")
                    : generalT("save")}
                </LoadingButton>
              </Stack>
            )}
          </form>
        </Stack>
      </Modal>
      {/* confirm close popup */}
      <ConfirmPopup
        open={toConfirmClose}
        handleClose={handleCancelConfirm}
        handleConfirm={onClose}
        title={generalT("discard")}
        subtitle={generalT("areYouSureToDiscardChanges")}
        confirmBtnText={generalT("confirm")}
      />
    </Box>
  );
};

export default AddEditJournalPopup;
